import React, { useRef, useState, useEffect, useCallback, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import CanvasGrid from '../CanvasGrid/index';
import ShapeRenderer from './ShapeRenderer';
import PreviewShape from './PreviewShape';
import FormulaGraph from '../FormulaGraph';
import UnifiedInfoPanel from '../UnifiedInfoPanel';
import { AnyShape, Point, OperationMode, ShapeType, MeasurementUnit, Triangle } from '@/types/shapes';
import { Formula, FormulaPoint } from '@/types/formula';
import { isGridDragging } from '../CanvasGrid/GridDragHandler';
import { 
  getStoredPixelsPerUnit, 
  DEFAULT_PIXELS_PER_CM, 
  DEFAULT_PIXELS_PER_MM, 
  DEFAULT_PIXELS_PER_INCH 
} from './CanvasUtils';
import {
  createHandleMouseDown,
  createHandleMouseMove,
  createHandleMouseUp,
  createHandleResizeStart,
  createHandleRotateStart,
  createHandleKeyDown
} from './CanvasEventHandlers';
import { RotateCw, Crosshair } from 'lucide-react';
import { ShapeServiceFactory } from '@/services/ShapeService';
import { getShapeMeasurements } from '@/utils/geometry/measurements';
import { GridZoomProvider, useGridZoom } from '@/contexts/GridZoomContext';
import { buildEvaluableExpression, createFunctionFromExpression, convertToLatex } from '@/utils/formulaUtils';

import { debugLog } from '@/utils/debug';
import { useTranslate } from '@/utils/translate';


// Add formula support to GeometryCanvas
interface FormulaCanvasProps extends GeometryCanvasProps {
  formulas?: Formula[]; // Use the Formula type from your types folder
  pixelsPerUnit?: number;
  serviceFactory?: ShapeServiceFactory;
  canvasTools?: React.ReactNode; // Add canvasTools prop
  isNonInteractive?: boolean; // Non-interactive when embedded
  showZoomControls?: boolean; // Add showZoomControls prop
  isEmbedded?: boolean; // Add isEmbedded prop for embedded iframe mode
  forceCenterOnInit?: boolean; // Center grid on first load when true
  showEmbeddedHeader?: boolean; // Controls the embedded title visibility
  showUnits?: boolean; // Show measurement units on grid
  showPositiveQuadrant?: boolean; // Lock grid to positive quadrant
  isGridCentered?: boolean; // Whether the grid is currently centered
  _paramSliderActive?: boolean; // transient signal from new parameter sliders
  _clearPanelSignal?: number; // bump to clear point and unified panel persistently
  onFirstInteractiveUse?: () => void;
}

interface GeometryCanvasProps {
  shapes: AnyShape[];
  selectedShapeId: string | null;
  activeMode: OperationMode;
  activeShapeType: ShapeType;
  measurementUnit: MeasurementUnit;
  isFullscreen?: boolean;
  gridPosition: Point | null;
  onShapeSelect: (id: string | null) => void;
  onShapeCreate: (start: Point, end: Point) => string;
  onShapeMove: (id: string, newPosition: Point) => void;
  onShapeResize: (id: string, factor: number) => void;
  onShapeRotate: (id: string, angle: number) => void;
  onShapeDelete?: (id: string) => void;
  onModeChange?: (mode: OperationMode) => void;
  onMoveAllShapes?: (dx: number, dy: number) => void;
  onGridPositionChange?: (newPosition: Point) => void;
  onMeasurementUpdate?: (key: string, value: string) => void;
  onFormulaSelect?: (formulaId: string) => void;
  positionUpdateId?: number;
  onResetGrid?: () => void;
}

const GeometryCanvas: React.FC<FormulaCanvasProps> = (props) => {
  return (
    <GridZoomProvider>
      <GeometryCanvasInner {...props} />
    </GridZoomProvider>
  );
};

const GeometryCanvasInner: React.FC<FormulaCanvasProps> = ({
  formulas = [],
  pixelsPerUnit: externalPixelsPerUnit = 0,
  shapes,
  selectedShapeId,
  activeMode,
  activeShapeType,
  measurementUnit,
  isFullscreen = false,
  gridPosition: externalGridPosition = null,
  onShapeSelect,
  onShapeCreate,
  onShapeMove,
  onShapeResize,
  onShapeRotate,
  onShapeDelete,
  onModeChange,
  onMoveAllShapes,
  onGridPositionChange,
  serviceFactory,
  onMeasurementUpdate,
  onFormulaSelect,
  canvasTools,
  isNonInteractive = false,
  showZoomControls = true,
  isEmbedded = false,
  forceCenterOnInit = false,
  showEmbeddedHeader = true,
  showUnits = true,
  showPositiveQuadrant = false,
  isGridCentered = false,
  positionUpdateId,
  onResetGrid,
  _paramSliderActive = false,
  _clearPanelSignal = 0,
  onFirstInteractiveUse = () => {}
}) => {
  const { zoomFactor, setZoomFactor } = useGridZoom();
  const t = useTranslate();
  const embeddedInitRef = useRef<{ grid: Point | null; zoom: number; formulas: Formula[] } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<Point | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<Point | null>(null);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [originalPosition, setOriginalPosition] = useState<Point | null>(null);
  const [resizeStart, setResizeStart] = useState<Point | null>(null);
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [rotateStart, setRotateStart] = useState<Point | null>(null);
  const [originalRotation, setOriginalRotation] = useState<number>(0);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0
  });
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  
  // Add state for calibration
  const [showCalibration, setShowCalibration] = useState(false);
  
  // State for pixel conversion values with persisted defaults
  const [pixelsPerUnit, setPixelsPerUnit] = useState(() => getStoredPixelsPerUnit(measurementUnit || 'cm'));
  const [pixelsPerSmallUnit, setPixelsPerSmallUnit] = useState(() => 
    measurementUnit === 'in' ? getStoredPixelsPerUnit('in') / 10 : DEFAULT_PIXELS_PER_MM
  );
  
  // Add a new state for persistent grid position - initialize as null to allow the CanvasGrid to center it
  const [gridPosition, setGridPosition] = useState<Point | null>(externalGridPosition || null);
  
  // Add state for selected formula point
  const [selectedPoint, setSelectedPoint] = useState<{
    x: number;
    y: number;
    mathX: number;
    mathY: number;
    formula: Formula;
    pointIndex?: number;
    allPoints?: FormulaPoint[];
    navigationStepSize?: number;
    isValid: boolean;
  } | null>(null);
  
  // Add persistent panel state to keep panel visible
  const [persistentPanelData, setPersistentPanelData] = useState<{
    type: 'point' | 'shape';
    point?: typeof selectedPoint;
    shapeId?: string | null;
    isVisible: boolean;
  } | null>(null);
  
  // Add state to track the current point index and all points for the selected formula
  const [_currentPointInfo, setCurrentPointInfo] = useState<{
    formulaId: string;
    pointIndex: number;
    allPoints: FormulaPoint[];
  } | null>(null);
  
  // Add a ref to track if we're clicking on a path
  const clickedOnPathRef = useRef(false);
  
  // Add a function to clear all selected points
  const clearAllSelectedPoints = useCallback(() => {
    // Clear the selected point in the GeometryCanvas
    setSelectedPoint(null);
    
    // Clear the current point info
    setCurrentPointInfo(null);
    
    // Reset the clicked on path flag
    clickedOnPathRef.current = false;
  }, []);
  
  // Function to close the persistent panel
  const closePersistentPanel = useCallback(() => {
    setPersistentPanelData(prev => {
      if (prev && prev.type === 'point' && prev.point) {
        // Preserve the exact selected point (with mathX/mathY) when closing the panel
        setSelectedPoint(prev.point);
      }
      return null;
    });
  }, []);

  // When parameter sliders are active, hide the unified panel temporarily without clearing selection
  useEffect(() => {
    if (_paramSliderActive) {
      setPersistentPanelData(prev => prev ? { ...prev, isVisible: false } : prev);
    } else {
      // When not active, show it again only if there is something selected
      setPersistentPanelData(prev => {
        if (!prev) return prev;
        return { ...prev, isVisible: true };
      });
    }
  }, [_paramSliderActive]);

  // Clear both selected point and persistent panel when signaled from parameter sliders
  useEffect(() => {
    if (_clearPanelSignal > 0) {
      clearAllSelectedPoints();
      setPersistentPanelData(null);
    }
  }, [_clearPanelSignal, clearAllSelectedPoints]);
  
  // Function to navigate to the next/previous point
  const navigateFormulaPoint = useCallback((direction: 'next' | 'previous', isShiftPressed = false) => {
    debugLog('navigateFormulaPoint direction:', direction, 'shift:', String(isShiftPressed));
    
    // Check both selectedPoint and persistentPanelData for the current point
    const currentPoint = selectedPoint || (persistentPanelData?.type === 'point' ? persistentPanelData.point : null);
    
    if (!currentPoint) {
      debugLog('No currentPoint, returning');
      return;
    }
    
    // Get the current point's mathematical X coordinate
    const currentMathX = currentPoint.mathX;
    
    // Round to 4 decimal places to handle floating point precision issues
    const roundedCurrentX = Math.round(currentMathX * 10000) / 10000;
    debugLog('Current mathX (rounded):', String(roundedCurrentX));
    
    // Determine the step size based on whether Shift is pressed
    // When Shift is pressed, we temporarily use 1.0, but we don't change the stored value
    const currentStepSize = currentPoint.navigationStepSize || 0.1;
    const stepSize = isShiftPressed ? 1.0 : currentStepSize;
    debugLog('Using step size:', String(stepSize), isShiftPressed ? '(Shift pressed)' : '');
    
    // Determine the target X coordinate based on direction
    let targetMathX;
    
    if (direction === 'previous') {
      // When going left, we want the previous increment
      targetMathX = Math.floor(roundedCurrentX / stepSize) * stepSize;
      if (Math.abs(targetMathX - roundedCurrentX) < 0.0001) {
        targetMathX -= stepSize;
      }
    } else { // next
      // When going right, we want the next increment
      targetMathX = Math.ceil(roundedCurrentX / stepSize) * stepSize;
      if (Math.abs(targetMathX - roundedCurrentX) < 0.0001) {
        targetMathX += stepSize;
      }
    }
    
    // Round to ensure we get exact increments
    targetMathX = Math.round(targetMathX / stepSize) * stepSize;
    
    // Round to 4 decimal places to handle floating point precision issues
    targetMathX = Math.round(targetMathX * 10000) / 10000;
    
    // In positive quadrant mode, clamp X to non-negative values
    if (showPositiveQuadrant && targetMathX < 0) {
      targetMathX = 0;
    }
    
    debugLog('Final target mathX:', targetMathX.toFixed(4));
    
    // Build the evaluable expression with implicit multiplication and variables substituted
    const formula = currentPoint.formula;
    const expression = buildEvaluableExpression(formula.expression, formula.variables, 'x');
    
    // Check if this is a formula with a potential singularity at x=0
    const hasSingularity = expression.includes('1/x') || 
                          expression.includes('/x') ||
                          expression.includes('x^-1') ||
                          expression.includes('x**-1') ||
                          expression.includes('Math.pow(x, -1)') ||
                          expression.includes('÷ x');
    
    // If we're trying to navigate across a singularity at x=0, skip over it
    if (hasSingularity && ((currentMathX < 0 && targetMathX >= 0) || (currentMathX > 0 && targetMathX <= 0))) {
      debugLog('Detected navigation across singularity at x=0, skipping over it');
      
      // Skip over zero with a small offset to avoid the singularity
      const skipAmount = stepSize * 2; // Use double the step size to ensure we clear the singularity
      targetMathX = (direction === 'next') ? Math.max(0.01, skipAmount) : Math.min(-0.01, -skipAmount);
      
      debugLog('Adjusted target mathX to skip singularity:', targetMathX.toFixed(4));
    }
    
    try {
      const fn = createFunctionFromExpression(expression, 1, currentPoint.formula.independentVar || 'x');
      let targetMathY = fn(targetMathX);
      
      // Check if the result is valid
      if (isNaN(targetMathY) || !isFinite(targetMathY)) {
        debugLog('Evaluated Y is not valid, trying to skip over singularity');
        
        // If we're at a singularity, try to skip over it
        if (hasSingularity) {
          // Try a few different offsets to find a valid point
          const offsets = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5];
          
          for (const offset of offsets) {
            // Apply offset in the direction we're moving
            const adjustedX = (direction === 'next') ? 
              targetMathX + offset : 
              targetMathX - offset;
            
            // Try the adjusted X value
            targetMathY = fn(adjustedX);
            
            // If we found a valid point, use it
            if (!isNaN(targetMathY) && isFinite(targetMathY)) {
              targetMathX = adjustedX;
              debugLog('Found valid point at adjusted x =', targetMathX.toFixed(4));
              break;
            }
          }
          
          // If we still don't have a valid point, give up
          if (isNaN(targetMathY) || !isFinite(targetMathY)) {
            debugLog('Could not find valid point even with adjustments');
            return;
          }
        } else {
          // For non-singularity functions, just return if the point is invalid
          return;
        }
      }
      
      // Apply the formula's scale factor
      const scaledMathY = targetMathY * formula.scaleFactor;
      
      // In positive quadrant mode, skip points with negative Y values
      if (showPositiveQuadrant && scaledMathY < 0) {
        debugLog('Skipping point with negative Y in positive quadrant mode');
        return;
      }
      
      // Get the zoomed pixels per unit value that includes the zoom factor
      const zoomedPixelsPerUnit = pixelsPerUnit * zoomFactor;
      
      // Convert from mathematical coordinates to canvas coordinates using zoomed pixels per unit
      const targetCanvasX = (gridPosition?.x || 0) + targetMathX * zoomedPixelsPerUnit;
      const targetCanvasY = (gridPosition?.y || 0) - scaledMathY * zoomedPixelsPerUnit;
      
      debugLog('Evaluated formula at x =', targetMathX.toFixed(4), 'y =', targetMathY.toFixed(4));
      debugLog('After scale factor:', String(formula.scaleFactor), 'y =', scaledMathY.toFixed(4));
      debugLog('Canvas coordinates:', targetCanvasX.toFixed(4), targetCanvasY.toFixed(4));
      
      // Create a new selected point with all the necessary information
      const newSelectedPoint = {
        ...currentPoint,
        x: targetCanvasX,
        y: targetCanvasY,
        mathX: targetMathX,
        mathY: scaledMathY,
        navigationStepSize: currentStepSize, // Preserve the original step size
        isValid: true
      };
      
      // Update the selected point
      setSelectedPoint(newSelectedPoint);
      
      // Store persistent panel data for the point
      setPersistentPanelData({
        type: 'point',
        point: newSelectedPoint,
        isVisible: true
      });
      
      // Focus the canvas to ensure keyboard events continue to work
      if (canvasRef.current) {
        canvasRef.current.focus();
      }
    } catch (error) {
      console.error('Error evaluating formula:', error);
    }
  }, [selectedPoint, persistentPanelData, gridPosition, pixelsPerUnit, zoomFactor]);
  
  // Effect to update internal grid position when external grid position changes
  useEffect(() => {
    // In positive quadrant mode, ignore all external grid position updates
    if (showPositiveQuadrant) {
      debugLog('GeometryCanvas: Ignoring external grid position - positive quadrant mode is active');
      return;
    }
    
    // Ignore external updates while user is actively dragging to avoid jump-backs
    if (isGridDragging.value) {
      return;
    }
    debugLog('GeometryCanvas: External grid position changed');
    
    // Skip if the positions are the same (using more precise comparison)
    if (gridPosition && externalGridPosition &&
        Math.abs(gridPosition.x - externalGridPosition.x) < 0.1 &&
        Math.abs(gridPosition.y - externalGridPosition.y) < 0.1) {
      return;
    }
    
    // If externalGridPosition is null, reset internal grid position to null
    if (externalGridPosition === null) {
      debugLog('GeometryCanvas: Resetting internal grid position to null');
      setGridPosition(null);
      return;
    }
    
    if (externalGridPosition) {
      debugLog('GeometryCanvas: Updating internal grid position from external');
      setGridPosition(externalGridPosition);
    }
  }, [externalGridPosition, gridPosition, showPositiveQuadrant]);
  
  // Add a ref to track if this is the first load
  const isFirstLoad = useRef(true);
  
  // Add a ref for debouncing grid position updates
  const gridPositionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Add state for Alt key
  const [isAltPressed, setIsAltPressed] = useState(false);
  
  // Track Shift and Alt key states
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      debugLog('Key down:', e.key);
      
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
      if (e.key === 'Alt') {
        setIsAltPressed(true);
      }
      
      // Note: Arrow key handling for formula point navigation is now done in the canvas onKeyDown handler
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
      if (e.key === 'Alt') {
        setIsAltPressed(false);
      }
    };
    
    // Add event listeners to the window
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    // Clean up event listeners on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  
  // Create a keyboard event handler for shape movement
  const handleShapeKeyDown = createHandleKeyDown({
    canvasRef,
    shapes,
    activeMode,
    activeShapeType,
    selectedShapeId,
    isDrawing,
    drawStart,
    drawCurrent,
    dragStart,
    originalPosition,
    resizeStart,
    originalSize,
    rotateStart,
    originalRotation,
    pixelsPerUnit,
    pixelsPerSmallUnit,
    measurementUnit,
    gridPosition,
    setIsDrawing,
    setDrawStart,
    setDrawCurrent,
    setDragStart,
    setOriginalPosition,
    setResizeStart,
    setOriginalSize,
    setRotateStart,
    setOriginalRotation,
    onShapeSelect,
    onShapeCreate,
    onShapeMove,
    onShapeResize,
    onShapeRotate,
    onShapeDelete,
    onModeChange,
    serviceFactory
  });
  
  // Function to focus the canvas container
  const focusCanvas = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.focus();
    }
  }, [canvasRef]);

  // Handle shape selection with focus
  const handleShapeSelect = useCallback((id: string) => {
    // Focus the canvas container so keyboard events work
    focusCanvas();
    
    // Call the original onShapeSelect function
    onShapeSelect(id);
  }, [onShapeSelect, focusCanvas]);
  
  // Handle calibration completion
  const _handleCalibrationComplete = (newPixelsPerUnit: number) => {
    debugLog('Calibration completed');
    
    // Store the calibrated value in localStorage
    localStorage.setItem(`pixelsPerUnit_${measurementUnit}`, newPixelsPerUnit.toString());
    debugLog('Stored new calibration value in localStorage');
    
    // Update the state
    setPixelsPerUnit(newPixelsPerUnit);
    
    // Update small unit value (1/10th of the main unit)
    const smallUnitValue = newPixelsPerUnit / 10;
    setPixelsPerSmallUnit(smallUnitValue);
    
    debugLog('Updated pixelsPerUnit/pixelsPerSmallUnit');
    
    // Force a redraw of the canvas
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setCanvasSize({
        width: rect.width,
        height: rect.height
      });
      debugLog('Canvas size set');
    }
    
    // Hide the calibration tool
    setShowCalibration(false);
  };
  
  // Update pixel values when measurement unit changes
  const prevMeasurementUnitRef = useRef(measurementUnit);
  const prevPixelsPerUnitRef = useRef(pixelsPerUnit);
  const canvasSizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Skip if nothing has changed
    if (measurementUnit === prevMeasurementUnitRef.current && 
        pixelsPerUnit === prevPixelsPerUnitRef.current) {
      return;
    }
    
    // Update refs
    prevMeasurementUnitRef.current = measurementUnit;
    prevPixelsPerUnitRef.current = pixelsPerUnit;
    
    debugLog('GeometryCanvas: Measurement unit changed');
    
    // Default to 'cm' if measurementUnit is undefined
    const unit = measurementUnit || 'cm';
    debugLog('Using unit:', unit);
    
    // Get the stored calibration value for this unit
    const storedValue = getStoredPixelsPerUnit(unit);
    debugLog('Retrieved stored pixels per unit');
    
    // Store the old pixels per unit value for conversion
    const oldPixelsPerUnit = pixelsPerUnit;
    
    // Check if the stored value is reasonable for the unit
    if (unit === 'in' && Math.abs(storedValue - DEFAULT_PIXELS_PER_CM) < 5) {
      // If inches value is suspiciously close to cm value, reset to default inches value
      debugLog('Detected incorrect inches calibration value, resetting to default');
      localStorage.setItem(`pixelsPerUnit_${unit}`, DEFAULT_PIXELS_PER_INCH.toString());
      setPixelsPerUnit(DEFAULT_PIXELS_PER_INCH);
      setPixelsPerSmallUnit(DEFAULT_PIXELS_PER_INCH / 10);
      debugLog('Updated inches calibration defaults');
    } else if (unit === 'cm' && Math.abs(storedValue - DEFAULT_PIXELS_PER_INCH) < 5) {
      // If cm value is suspiciously close to inches value, reset to default cm value
      debugLog('Detected incorrect cm calibration value, resetting to default');
      localStorage.setItem(`pixelsPerUnit_${unit}`, DEFAULT_PIXELS_PER_CM.toString());
      setPixelsPerUnit(DEFAULT_PIXELS_PER_CM);
      setPixelsPerSmallUnit(DEFAULT_PIXELS_PER_MM);
      debugLog('Updated cm calibration defaults');
    } else {
      // Update the pixel values without affecting the grid position
      setPixelsPerUnit(storedValue);
      
      const smallUnitValue = storedValue / 10;
      setPixelsPerSmallUnit(smallUnitValue);
    }
    
    // Convert shape dimensions to maintain physical size
    if (shapes && shapes.length > 0 && oldPixelsPerUnit > 0) {
      // Calculate the conversion factor based on the unit change
      // When switching from cm to inches: 1 inch = 2.54 cm
      // When switching from inches to cm: 1 cm = 0.3937 inches
      const cmToInchFactor = 2.54;
      const inchToCmFactor = 1 / cmToInchFactor;
      
      let conversionFactor;
      if (unit === 'in' && measurementUnit !== unit) {
        // Converting from cm to inches
        conversionFactor = (storedValue / oldPixelsPerUnit) * inchToCmFactor;
      } else if (unit === 'cm' && measurementUnit !== unit) {
        // Converting from inches to cm
        conversionFactor = (storedValue / oldPixelsPerUnit) * cmToInchFactor;
      } else {
        // Same unit, just different calibration
        conversionFactor = storedValue / oldPixelsPerUnit;
      }
      
      debugLog('Converting shapes with factor');
      
      // Update shapes through the parent component
      if (onShapeResize) {
        shapes.forEach(shape => {
          if (shape.id) {
            // Apply the conversion factor to maintain physical dimensions
            onShapeResize(shape.id, conversionFactor);
          }
        });
      }
    }
    
    // Debounce the canvas size update
    if (canvasSizeTimeoutRef.current) {
      clearTimeout(canvasSizeTimeoutRef.current);
    }
    
    canvasSizeTimeoutRef.current = setTimeout(() => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setCanvasSize({
          width: rect.width,
          height: rect.height
        });
      }
      canvasSizeTimeoutRef.current = null;
    }, 100);
  }, [measurementUnit, pixelsPerUnit, onShapeResize, shapes]);

  // Clean up any ongoing operations when the active mode changes
  useEffect(() => {
    setIsDrawing(false);
    setDrawStart(null);
    setDrawCurrent(null);
    setDragStart(null);
    setOriginalPosition(null);
    setResizeStart(null);
    setOriginalSize(null);
    setRotateStart(null);
    setOriginalRotation(0);
  }, [activeMode]);

  // Measure canvas on mount and resize
  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasRef.current) {
        const { width, height } = canvasRef.current.getBoundingClientRect();
        setCanvasSize({ width, height });
      }
    };
    
    // Debounced resize handler for better performance
    let resizeTimer: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        updateCanvasSize();
        // In embedded mode, only update once to prevent flicker
        if (!isEmbedded) {
          // Add a second update after a short delay to catch any post-resize adjustments
          setTimeout(updateCanvasSize, 100);
        }
      }, 100);
    };
    
    // Initial update
    updateCanvasSize();
    
    // Update after a short delay to ensure the DOM has fully rendered
    const initialTimeout = setTimeout(updateCanvasSize, 100);
    
    // Update on window resize with debouncing
    window.addEventListener('resize', debouncedResize);
    
    // Also update on scroll events, as they might affect the viewport
    window.addEventListener('scroll', debouncedResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('scroll', debouncedResize);
      clearTimeout(initialTimeout);
      clearTimeout(resizeTimer);
    };
  }, [isEmbedded]); // Only run on mount and when embedded mode changes

  // Create mouse event handlers
  const handleMouseDown = createHandleMouseDown({
    canvasRef,
    shapes,
    activeMode,
    activeShapeType,
    selectedShapeId,
    isDrawing,
    drawStart,
    drawCurrent,
    dragStart,
    originalPosition,
    resizeStart,
    originalSize,
    rotateStart,
    originalRotation,
    pixelsPerUnit,
    pixelsPerSmallUnit,
    measurementUnit,
    gridPosition,
    setIsDrawing,
    setDrawStart,
    setDrawCurrent,
    setDragStart,
    setOriginalPosition,
    setResizeStart,
    setOriginalSize,
    setRotateStart,
    setOriginalRotation,
    onShapeSelect: (id: string | null) => {
      // Focus the canvas when a shape is selected
      if (id !== null) {
        focusCanvas();
      }
      onShapeSelect(id);
    },
    onShapeCreate,
    onShapeMove,
    onShapeResize,
    onShapeRotate,
    onModeChange,
    serviceFactory
  });
  
  // Create keyboard event handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Check both selectedPoint and persistentPanelData for the current point
    const currentPoint = selectedPoint || (persistentPanelData?.type === 'point' ? persistentPanelData.point : null);
    
    // Handle existing keyboard events
    if (currentPoint) {
      if (e.key === 'ArrowLeft') {
        debugLog('Canvas ArrowLeft pressed, shift:', String(e.shiftKey));
        e.preventDefault();
        e.stopPropagation();
        navigateFormulaPoint('previous', e.shiftKey);
      } else if (e.key === 'ArrowRight') {
        debugLog('Canvas ArrowRight pressed, shift:', String(e.shiftKey));
        e.preventDefault();
        e.stopPropagation();
        navigateFormulaPoint('next', e.shiftKey);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // Handle up/down arrow keys to adjust the navigation step size
        e.preventDefault();
        e.stopPropagation(); // Prevent global handler from also processing this
        
        // Get the current step size
        const currentStepSize = currentPoint.navigationStepSize || 0.1;
        
        // Calculate the new step size
        let newStepSize = currentStepSize;
        if (e.key === 'ArrowUp') {
          // Increase step size
          newStepSize = Math.min(1.0, currentStepSize + 0.01);
        } else {
          // Decrease step size
          newStepSize = Math.max(0.01, currentStepSize - 0.01);
        }
        
        debugLog('Adjusting step size');
        
        // Update both selectedPoint and persistentPanelData
        const updatedPoint = { ...currentPoint, navigationStepSize: newStepSize };
        setSelectedPoint(updatedPoint);
        if (persistentPanelData?.type === 'point') {
          setPersistentPanelData({
            ...persistentPanelData,
            point: updatedPoint
          });
        }
      }
    } else if (selectedShapeId) {
      // If a shape is selected and no formula point is selected, use the shape movement handler
      handleShapeKeyDown(e as unknown as KeyboardEvent);
    }

    // Handle zoom reset (Ctrl/Cmd + 0)
    if ((e.ctrlKey || e.metaKey) && e.key === '0') {
      e.preventDefault();
      setZoomFactor(1);
    }
  };

  // Simple key up handler
  const handleKeyUp = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    // Add keyboard handling logic if needed
    debugLog('Key up:', e.key);
  }, []);

  // Global keyboard support is no longer needed since we restore focus on point selection
  // The canvas keyboard handler will handle all point navigation when focused

  // Add a ref to track if we're already updating the grid position
  const _isUpdatingGridPositionRef = useRef(false);

  // Removed verbose logging and unnecessary batching on grid move to improve drag performance
  
  // Clear selected points when a shape is selected and store persistent panel data
  useEffect(() => {
    if (selectedShapeId) {
      clearAllSelectedPoints();
      
      // Store persistent panel data for the shape
      setPersistentPanelData({
        type: 'shape',
        shapeId: selectedShapeId,
        isVisible: true
      });
    } else {
      // If no shape is selected, clear shape persistent data
      setPersistentPanelData(prev => {
        if (prev?.type === 'shape') {
          return null;
        }
        return prev;
      });
    }
  }, [selectedShapeId, clearAllSelectedPoints]);

  // Handle mouse move
  const handleMouseMove = createHandleMouseMove({
    canvasRef,
    shapes,
    activeMode,
    activeShapeType,
    selectedShapeId,
    isDrawing,
    drawStart,
    drawCurrent,
    dragStart,
    originalPosition,
    resizeStart,
    originalSize,
    rotateStart,
    originalRotation,
    pixelsPerUnit,
    pixelsPerSmallUnit,
    measurementUnit,
    gridPosition,
    setIsDrawing,
    setDrawStart,
    setDrawCurrent,
    setDragStart,
    setOriginalPosition,
    setResizeStart,
    setOriginalSize,
    setRotateStart,
    setOriginalRotation,
    onShapeSelect: (id: string | null) => {
      // Focus the canvas when a shape is selected during mouse move
      if (id !== null) {
        focusCanvas();
      }
      onShapeSelect(id);
    },
    onShapeCreate,
    onShapeMove,
    onShapeResize,
    onShapeRotate,
    onModeChange,
    serviceFactory
  });

  const handleMouseUp = createHandleMouseUp({
    canvasRef,
    shapes,
    activeMode,
    activeShapeType,
    selectedShapeId,
    isDrawing,
    drawStart,
    drawCurrent,
    dragStart,
    originalPosition,
    resizeStart,
    originalSize,
    rotateStart,
    originalRotation,
    pixelsPerUnit,
    pixelsPerSmallUnit,
    measurementUnit,
    gridPosition,
    zoomFactor,
    setIsDrawing,
    setDrawStart,
    setDrawCurrent,
    setDragStart,
    setOriginalPosition,
    setResizeStart,
    setOriginalSize,
    setRotateStart,
    setOriginalRotation,
    onShapeSelect: (id: string | null) => {
      // Focus the canvas when a shape is selected during mouse up
      if (id !== null) {
        focusCanvas();
      }
      onShapeSelect(id);
    },
    onShapeCreate,
    onShapeMove,
    onShapeResize,
    onShapeRotate,
    onModeChange,
    serviceFactory
  });

  const _handleResizeStart = createHandleResizeStart({
    canvasRef,
    shapes,
    activeMode,
    activeShapeType,
    selectedShapeId,
    isDrawing,
    drawStart,
    drawCurrent,
    dragStart,
    originalPosition,
    resizeStart,
    originalSize,
    rotateStart,
    originalRotation,
    pixelsPerUnit,
    pixelsPerSmallUnit,
    measurementUnit,
    setIsDrawing,
    setDrawStart,
    setDrawCurrent,
    setDragStart,
    setOriginalPosition,
    setResizeStart,
    setOriginalSize,
    setRotateStart,
    setOriginalRotation,
    onShapeSelect: (id: string | null) => {
      // Focus the canvas when a shape is selected for resizing
      if (id !== null) {
        focusCanvas();
      }
      onShapeSelect(id);
    },
    onShapeCreate,
    onShapeMove,
    onShapeResize,
    onShapeRotate,
    onModeChange,
    serviceFactory
  });

  const handleRotateStart = createHandleRotateStart({
    canvasRef,
    shapes,
    activeMode,
    activeShapeType,
    selectedShapeId,
    isDrawing,
    drawStart,
    drawCurrent,
    dragStart,
    originalPosition,
    resizeStart,
    originalSize,
    rotateStart,
    originalRotation,
    pixelsPerUnit,
    pixelsPerSmallUnit,
    measurementUnit,
    setIsDrawing,
    setDrawStart,
    setDrawCurrent,
    setDragStart,
    setOriginalPosition,
    setResizeStart,
    setOriginalSize,
    setRotateStart,
    setOriginalRotation,
    onShapeSelect: (id: string | null) => {
      // Focus the canvas when a shape is selected for rotation
      if (id !== null) {
        focusCanvas();
      }
      onShapeSelect(id);
    },
    onShapeCreate,
    onShapeMove,
    onShapeResize,
    onShapeRotate,
    onModeChange,
    serviceFactory
  });

  // Toggle calibration tool
  const _toggleCalibration = () => {
    setShowCalibration(!showCalibration);
  };

  // Inside the GeometryCanvas component, add a function to handle moving all shapes
  const handleMoveAllShapes = useCallback((dx: number, dy: number) => {
    if (!onMoveAllShapes) return;
    
    // Call the parent component's handler with precise deltas
    onMoveAllShapes(dx, dy);
  }, [onMoveAllShapes]);
  
  // Clear selected points when mode changes
  useEffect(() => {
    clearAllSelectedPoints();
  }, [activeMode, clearAllSelectedPoints]);
  
  // Keep selected formula point across grid moves/scroll; do not clear on grid position changes
  // (Removed previous effect that cleared selection here)

  // Handle grid position change
  const handleGridPositionChange = useCallback((newPosition: Point) => {
    debugLog('GeometryCanvas: Grid position changed');
    
    // In embedded mode with positive quadrant, ignore position updates from resize events
    if (isEmbedded && showPositiveQuadrant) {
      const expectedBottom = Math.round(canvasSize.height);
      // Only accept the update if it's significantly different from the expected bottom-left position
      if (Math.abs(newPosition.x) < 5 && Math.abs(newPosition.y - expectedBottom) < 5) {
        return; // Ignore minor variations that could cause flickering
      }
    }
    
    // Only update if the position has actually changed
    if (!gridPosition || newPosition.x !== gridPosition.x || newPosition.y !== gridPosition.y) {
      // Debounce the grid position updates for parent notification, but update local state immediately
      if (gridPositionTimeoutRef.current) {
        clearTimeout(gridPositionTimeoutRef.current);
      }
      
      // Update the grid position immediately to ensure formulas update in real-time
      setGridPosition(newPosition);
      
      // Notify parent after a delay to prevent too many updates
      gridPositionTimeoutRef.current = setTimeout(() => {
        // If we have a parent handler for grid position changes, call it
        if (onGridPositionChange) {
          debugLog('GeometryCanvas: Notifying parent of grid position change (debounced)');
          onGridPositionChange(newPosition);
        }
        gridPositionTimeoutRef.current = null;
      }, 100); // Reduced from 200ms to 100ms for more responsive updates
    } else {
      debugLog('GeometryCanvas: Skipping grid position update (no change)');
    }
  }, [onGridPositionChange, gridPosition, isEmbedded, showPositiveQuadrant, canvasSize.height]);

  // Clean up the timeout when the component unmounts
  useEffect(() => {
    return () => {
      if (gridPositionTimeoutRef.current) {
        clearTimeout(gridPositionTimeoutRef.current);
      }
    };
  }, []);

  // Hide canvas until first-load initialization completes to avoid visible jump
  const [didInit, setDidInit] = useState(false);

  // Ensure the grid is centered on first load only if no initial grid exists in URL or props
  useEffect(() => {
    if (canvasSize.width > 0 && canvasSize.height > 0 && isFirstLoad.current) {
      debugLog('GeometryCanvas: First load with valid canvas size');
      
      // Respect existing grid from URL
      const url = new URL(window.location.href);
      const hasGridInUrl = url.searchParams.has('grid');
      
      if (isEmbedded || (gridPosition === null && !hasGridInUrl)) {
        if (showPositiveQuadrant) {
          // In positive-quadrant mode, do NOT center; lock to bottom-left once
          // In embedded mode, account for padding
          const padding = isEmbedded ? 12 : 0;
          const bottomLeft = { x: padding, y: Math.round(canvasSize.height - padding) };
          setGridPosition(bottomLeft);
          setZoomFactor(isEmbedded ? 0.7 : 1);
        } else {
          debugLog('GeometryCanvas: No initial grid position, centering grid');
          const centeredPosition = {
            x: Math.round(canvasSize.width / 2),
            y: Math.round(canvasSize.height / 2)
          };
          setGridPosition(centeredPosition);
          setZoomFactor(1);
        }
      }
      
      // Save initial snapshot for embedded reset
      if (isEmbedded && !embeddedInitRef.current) {
        const padding = 12;
        embeddedInitRef.current = {
          grid: showPositiveQuadrant ? { x: padding, y: Math.round(canvasSize.height - padding) } : {
            x: Math.round(canvasSize.width / 2),
            y: Math.round(canvasSize.height / 2)
          },
          zoom: isEmbedded ? 0.7 : 1,
          formulas: JSON.parse(JSON.stringify(formulas))
        };
      }

      // Mark first load as complete
      isFirstLoad.current = false;
      setDidInit(true);
    }
  }, [canvasSize, gridPosition, isEmbedded, setZoomFactor, formulas, showPositiveQuadrant]);
  
  // Effect to reposition grid when switching between embedded and normal modes
  const [lastModeState, setLastModeState] = useState({ isEmbedded, showPositiveQuadrant });
  
  useEffect(() => {
    if (canvasSize.width > 0 && canvasSize.height > 0 && !isFirstLoad.current) {
      // Only update if mode actually changed (not just canvas size)
      const modeChanged = lastModeState.isEmbedded !== isEmbedded || 
                         lastModeState.showPositiveQuadrant !== showPositiveQuadrant;
      
      if (modeChanged) {
        setLastModeState({ isEmbedded, showPositiveQuadrant });
        
        if (showPositiveQuadrant) {
          // Always lock to bottom-left in positive quadrant mode
          // In embedded mode, account for padding
          const padding = isEmbedded ? 12 : 0;
          const bottomLeft = { x: padding, y: Math.round(canvasSize.height - padding) };
          setGridPosition(bottomLeft);
        } else if (forceCenterOnInit) {
          // Re-center when switching modes if forceCenterOnInit is true
          const centeredPosition = {
            x: Math.round(canvasSize.width / 2),
            y: Math.round(canvasSize.height / 2)
          };
          setGridPosition(centeredPosition);
        }
        // Update zoom based on mode
        setZoomFactor(isEmbedded ? 0.7 : 1);
      }
    }
  }, [isEmbedded, canvasSize, forceCenterOnInit, showPositiveQuadrant, setZoomFactor, lastModeState]);

  // Handle formula point selection
  const handleFormulaPointSelect = (point: {
    x: number;
    y: number;
    mathX: number; 
    mathY: number;
    formula: Formula;
    pointIndex?: number;
    allPoints?: FormulaPoint[];
    navigationStepSize?: number;
    isValid: boolean;
  } | null) => {
    // Optional debug
    if (point) {
      debugLog('Point selected');
    } else {
      debugLog('Point selection cleared');
    }
    
    // Handle point selection
    if (point) {
      // Set the clicked on path flag to true
      clickedOnPathRef.current = true;
      
      // Always ensure navigationStepSize has a default value
      // The point's mathX and mathY properties already account for the zoom factor
      // because they were calculated using the pixelsPerUnit value which includes zoom
      const pointWithStepSize = {
        ...point,
        navigationStepSize: point.navigationStepSize || 0.1,
        isValid: true
      };
      
      setSelectedPoint(pointWithStepSize);
      
      // Store persistent panel data for the point
      setPersistentPanelData({
        type: 'point',
        point: pointWithStepSize,
        isVisible: true
      });
      
      // Store the current point index and all points if provided
      if (point.pointIndex !== undefined && point.allPoints) {
        setCurrentPointInfo({
          formulaId: point.formula.id,
          pointIndex: point.pointIndex,
          allPoints: point.allPoints
        });
      } else {
        setCurrentPointInfo(null);
      }
      
      // Select the formula in the function tool
      if (onFormulaSelect) {
        onFormulaSelect(point.formula.id);
      }
      
      // Switch to select mode
      if (onModeChange) {
        onModeChange('select');
      }
      // Ensure keyboard navigation works immediately
      if (canvasRef.current) {
        canvasRef.current.focus();
      }
    } else {
      setSelectedPoint(null);
      setCurrentPointInfo(null);
      setPersistentPanelData(null);
    }
  };

  // Local copy for embedded mode so sliders can mutate values without touching parent state
  const [embeddedFormulas, setEmbeddedFormulas] = useState<Formula[]>(formulas);
  // Use refs to track previous values and avoid loops
  const prevFormulasRef = useRef<Formula[] | null>(null);
  
  // Callback to update persistent panel when formulas change
  const updatePersistentPanelForFormulas = useCallback(() => {
    if (persistentPanelData?.type === 'point' && persistentPanelData.point && formulas && formulas.length > 0 && gridPosition) {
      const point = persistentPanelData.point;
      const formula = point.formula;
      
      // Only update if formulas actually changed (avoid loops)
      const formulasChanged = !prevFormulasRef.current || 
        prevFormulasRef.current.length !== formulas.length ||
        prevFormulasRef.current.some((prevFormula, index) => 
          formulas[index]?.expression !== prevFormula.expression ||
          formulas[index]?.scaleFactor !== prevFormula.scaleFactor
        );
      
      if (formulasChanged) {
        // Find the matching formula in the new formulas array
        const updatedFormula = formulas.find(f => f.id === formula.id);
        if (updatedFormula) {
          try {
            // Use the simple function evaluation to avoid complex dependencies
            const fn = createFunctionFromExpression(updatedFormula.expression, updatedFormula.scaleFactor, updatedFormula.independentVar || 'x');
            const newMathY = fn(point.mathX);
            
            if (!isNaN(newMathY) && isFinite(newMathY)) {
              const currentZoomedPixelsPerUnit = (externalPixelsPerUnit || pixelsPerUnit) * zoomFactor;
              const newCanvasY = gridPosition.y - (newMathY * currentZoomedPixelsPerUnit);
              
              const updatedPoint = {
                ...point,
                formula: updatedFormula,
                mathY: newMathY,
                y: newCanvasY
              };
              
              // Update persistent panel data without triggering loops
              setPersistentPanelData(prev => 
                prev ? { ...prev, point: updatedPoint } : prev
              );
            }
          } catch (error) {
            console.error('Error updating persistent point:', error);
          }
        }
      }
    }
    
    // Store current formulas for comparison in next render
    prevFormulasRef.current = formulas ? [...formulas] : null;
  }, [persistentPanelData, formulas, gridPosition, externalPixelsPerUnit, pixelsPerUnit, zoomFactor]);
  
  // Effect to log when formulas change and update persistent panel
  useEffect(() => {
    // In embedded mode, do not overwrite current embeddedFormulas when grid moves or parent re-renders.
    // Only sync when the actual formulas array identity/content changes meaningfully.
    if (!isEmbedded) {
      setEmbeddedFormulas(formulas);
    } else if (!prevFormulasRef.current || prevFormulasRef.current.length !== formulas.length ||
      prevFormulasRef.current.some((f, i) => f.id !== formulas[i]?.id || f.expression !== formulas[i]?.expression)) {
      setEmbeddedFormulas(formulas);
    }
    setSelectedPoint(null);
    updatePersistentPanelForFormulas();
  }, [formulas, isEmbedded, updatePersistentPanelForFormulas]);

  // Render formulas function
  const renderFormulas = () => {
    const list = isEmbedded ? embeddedFormulas : formulas;
    if (!list || list.length === 0 || !gridPosition) {
      return null;
    }

    // Apply visual padding for positive-quadrant so graphs align with padded grid lines
    const paddingPx = showPositiveQuadrant && isEmbedded ? 12 : 0;
    const effectiveGridPosition = showPositiveQuadrant
      ? { x: gridPosition.x, y: gridPosition.y }  // Grid position already includes padding
      : gridPosition;

    // In embedded mode, show only a single graph: the primary formula displayed above
    if (isEmbedded) {
      const primary = list[0];
      if (!primary) return null;
      return (
        <FormulaGraph
          key={primary.id}
          formula={primary}
          gridPosition={effectiveGridPosition}
          pixelsPerUnit={zoomedPixelsPerUnit}
          showPositiveQuadrant={showPositiveQuadrant}
          onPointSelect={undefined}
          globalSelectedPoint={undefined}
          hideSelectedPoint={true}
          isEmbedded={true}
        />
      );
    }

    // Default: render all formulas in standard mode
    return list.map(formula => (
      <FormulaGraph
        key={formula.id}
        formula={formula}
        gridPosition={effectiveGridPosition}
        pixelsPerUnit={zoomedPixelsPerUnit}
        showPositiveQuadrant={showPositiveQuadrant}
        onPointSelect={handleFormulaPointSelect}
        globalSelectedPoint={selectedPoint || (persistentPanelData?.type === 'point' ? persistentPanelData.point : null)}
        hideSelectedPoint={_paramSliderActive}
        isEmbedded={false}
      />
    ));
  };

  // Helper functions to get shape dimensions
  const _getShapeWidth = (shape: AnyShape): number => {
    let xValues: number[] = [];
    
    switch (shape.type) {
      case 'circle':
        return shape.radius * 2;
      case 'rectangle':
        return shape.width;
      case 'triangle':
        // Calculate width from points
        xValues = shape.points.map(p => p.x);
        return Math.max(...xValues) - Math.min(...xValues);
      case 'line':
        // Calculate width from start and end points
        return Math.abs(shape.endPoint.x - shape.startPoint.x);
      default:
        return 0;
    }
  };

  const _getShapeHeight = (shape: AnyShape): number => {
    let yValues: number[] = [];
    
    switch (shape.type) {
      case 'circle':
        return shape.radius * 2;
      case 'rectangle':
        return shape.height;
      case 'triangle':
        // Calculate height from points
        yValues = shape.points.map(p => p.y);
        return Math.max(...yValues) - Math.min(...yValues);
      case 'line':
        // Calculate height from start and end points
        return Math.abs(shape.endPoint.y - shape.startPoint.y);
      default:
        return 0;
    }
  };

  // Create a custom mouseUp handler that combines the existing handleMouseUp with point dismissal logic
  const customMouseUpHandler = (e: React.MouseEvent) => {
    if (isGridDragging.value) {
      return;
    }
    
    handleMouseUp(e);
    
    if (e.type === 'mouseup') {
      if ((e.target as Element).tagName === 'path') {
        clickedOnPathRef.current = true;
        return;
      }
      
      // Do not clear when clicking inside the unified info panel
      const unifiedPanel = document.querySelector('.unified-info-panel-container');
      if (unifiedPanel && unifiedPanel.contains(e.target as Node)) {
        return;
      }
      
      const infoBox = document.querySelector('.formula-point-info');
      if (infoBox && infoBox.contains(e.target as Node)) {
        return;
      }
      
      const navigationButtons = document.querySelectorAll('.point-nav-button, [data-nav-button]');
      for (const button of navigationButtons) {
        if (button === e.target || button.contains(e.target as Node)) {
          return;
        }
      }
      
      const toolButton = document.querySelector('.btn-tool');
      if (toolButton && (toolButton === e.target || toolButton.contains(e.target as Node))) {
        return;
      }
      
      clearAllSelectedPoints();
      clickedOnPathRef.current = false;
    }
  };

  // Add a global handler to ensure the isGridDragging flag is properly reset
  useEffect(() => {
    // We're removing the global mouseup handler that resets isGridDragging
    // because it's causing conflicts with the GridDragHandler component.
    // The GridDragHandler will handle resetting isGridDragging itself.
    
    // No longer adding a global mouseup handler here
    
    return () => {
      // No cleanup needed
    };
  }, []);

  // Effect to focus the canvas when selectedPoint changes
  useEffect(() => {
    if (selectedPoint && canvasRef.current) {
      debugLog('selectedPoint changed, focusing canvas');
      canvasRef.current.focus();
    }
  }, [selectedPoint]);

  // Get measurements for the selected shape
  const getMeasurementsForSelectedShape = useCallback((): Record<string, string> => {
    const shapeId = persistentPanelData?.type === 'shape' ? persistentPanelData.shapeId : null;
    if (!shapeId) return {};
    
    const selectedShape = shapes.find(s => s.id === shapeId);
    if (!selectedShape) return {};
    
    // Convert pixels to the current measurement unit
    const convertFromPixels = (pixels: number): number => {
      return pixels / (externalPixelsPerUnit || pixelsPerUnit);
    };
    
    // Get the measurements as numbers
    const measurementsAsNumbers = getShapeMeasurements(selectedShape, convertFromPixels);
    
    // Convert to strings for display
    const measurementsAsStrings: Record<string, string> = {};
    Object.entries(measurementsAsNumbers).forEach(([key, value]) => {
      measurementsAsStrings[key] = value.toString();
    });
    
    return measurementsAsStrings;
  }, [persistentPanelData, shapes, externalPixelsPerUnit, pixelsPerUnit]);
  
  // Use a ref to store the current measurements
  const measurementsRef = useRef<Record<string, string>>({});
  
  // Add a state to track measurements for the UnifiedInfoPanel
  const [currentPanelMeasurements, setCurrentPanelMeasurements] = useState<Record<string, string>>({});
  
  // Update measurements when shapes or persistentPanelData changes
  useEffect(() => {
    if (persistentPanelData?.type === 'shape' && persistentPanelData.shapeId) {
      const newMeasurements = getMeasurementsForSelectedShape();
      measurementsRef.current = newMeasurements;
      setCurrentPanelMeasurements(newMeasurements);
    } else {
      measurementsRef.current = {};
      setCurrentPanelMeasurements({});
    }
  }, [shapes, persistentPanelData, getMeasurementsForSelectedShape]);

  // Handle measurement updates
  const handleMeasurementUpdate = (key: string, value: string): void => {
    const shapeId = persistentPanelData?.type === 'shape' ? persistentPanelData.shapeId : null;
    if (!shapeId) return;
    
    // Instead of trying to handle the update internally, pass it to the parent component
    // The parent component has access to the shape services that know how to properly update each shape type
    if (onMeasurementUpdate) {
      onMeasurementUpdate(key, value);
      return;
    }
    
    // If no onMeasurementUpdate is provided, fall back to the basic implementation
    const selectedShape = shapes.find(s => s.id === shapeId);
    if (!selectedShape) return;
    
    // Convert the value to a number
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return;
    
    // Convert from measurement unit to pixels
    const convertToPixels = (unitValue: number): number => {
      return unitValue * (externalPixelsPerUnit || pixelsPerUnit);
    };
    
    // Create a copy of the shape to modify
    const updatedShape = { ...selectedShape };
    
    // Update the shape based on the measurement key
    switch (key) {
      case 'width':
        if ('width' in updatedShape) {
          updatedShape.width = convertToPixels(numericValue);
        }
        break;
      case 'height':
        if ('height' in updatedShape) {
          updatedShape.height = convertToPixels(numericValue);
        }
        break;
      case 'radius':
        if ('radius' in updatedShape) {
          updatedShape.radius = convertToPixels(numericValue);
        }
        break;
      case 'diameter':
        if ('radius' in updatedShape) {
          // Diameter is twice the radius
          updatedShape.radius = convertToPixels(numericValue) / 2;
        }
        break;
      case 'circumference':
        if ('radius' in updatedShape) {
          // Circumference = 2πr, so r = C/(2π)
          updatedShape.radius = convertToPixels(numericValue) / (2 * Math.PI);
        }
        break;
      case 'angle':
        if ('rotation' in updatedShape) {
          updatedShape.rotation = numericValue;
        }
        break;
    }
    
    // Update the shape in the parent component
    if (onShapeResize && 'width' in updatedShape && 'width' in selectedShape) {
      // For width/height changes, calculate the resize factor
      const widthFactor = updatedShape.width / selectedShape.width;
      onShapeResize(shapeId, widthFactor);
    } else if (onShapeResize && 'radius' in updatedShape && 'radius' in selectedShape) {
      // For radius changes, calculate the resize factor
      const radiusFactor = updatedShape.radius / selectedShape.radius;
      onShapeResize(shapeId, radiusFactor);
    } else if (onShapeRotate && 'rotation' in updatedShape && 'rotation' in selectedShape) {
      // For rotation changes
      onShapeRotate(shapeId, updatedShape.rotation);
    } else if (key.startsWith('side') || key.startsWith('angle') || key === 'length') {
      // For triangle sides, angles, and line lengths, we need a special approach
      // Trigger a small movement to force a redraw with the updated shape
      onShapeMove(shapeId, { x: 0, y: 0 });
    }

    // After updating the shape, immediately update the measurements panel
    if (shapeId) {
      setTimeout(() => {
        const updatedMeasurements = getMeasurementsForSelectedShape();
        measurementsRef.current = updatedMeasurements;
        setCurrentPanelMeasurements(updatedMeasurements);
      }, 10);
    }
  };

  // Add cleanup in a useEffect
  useEffect(() => {
    return () => {
      if (canvasSizeTimeoutRef.current) {
        clearTimeout(canvasSizeTimeoutRef.current);
      }
    };
  }, []);

  // Apply zoom factor to pixel values
  const zoomedPixelsPerUnit = (externalPixelsPerUnit || pixelsPerUnit) * zoomFactor;
  const zoomedPixelsPerSmallUnit = zoomedPixelsPerUnit / 10;

  // Scale shapes according to zoom factor
  const scaledShapes = shapes.map(shape => {
    
    
    // Base shape with unmodified position
    const baseShape = {
      ...shape,
      position: shape.position // Keep original position
    };

    let scaledShape: AnyShape;
    // Declare variables used in case blocks
    let originalRadius: number;
    let originalWidth: number;
    let originalHeight: number;
    let originalPoints: Point[];
    let center: Point;
    let scaledPoints: Point[];
    let originalDx: number;
    let originalDy: number;
    let triangleShape: Triangle;

    // If this is the first time scaling this shape, store original dimensions
    if (!shape.originalDimensions) {
      switch (shape.type) {
        case 'circle':
          shape.originalDimensions = { radius: shape.radius };
          break;
        case 'rectangle':
          shape.originalDimensions = { width: shape.width, height: shape.height };
          break;
        case 'triangle':
          shape.originalDimensions = { points: [...shape.points] };
          break;
        case 'line':
          shape.originalDimensions = { 
            dx: shape.endPoint.x - shape.position.x,
            dy: shape.endPoint.y - shape.position.y
          };
          break;
      }
    }

    // Handle specific shape types
    switch (shape.type) {
      case 'circle':
        // Scale radius from original size
        originalRadius = shape.originalDimensions?.radius || shape.radius;
        scaledShape = {
          ...baseShape,
          radius: originalRadius * zoomFactor,
          scaleFactor: zoomFactor,
          originalDimensions: shape.originalDimensions || { radius: shape.radius }
        } as AnyShape;
        break;

      case 'rectangle':
        // Scale dimensions from original size
        originalWidth = shape.originalDimensions?.width || shape.width;
        originalHeight = shape.originalDimensions?.height || shape.height;
        scaledShape = {
          ...baseShape,
          width: originalWidth * zoomFactor,
          height: originalHeight * zoomFactor,
          scaleFactor: zoomFactor,
          originalDimensions: shape.originalDimensions || { width: shape.width, height: shape.height }
        } as AnyShape;
        break;

      case 'triangle':
        triangleShape = shape as Triangle;
        // Get original points
        originalPoints = triangleShape.originalDimensions?.points || triangleShape.points;
        
        // Calculate center from original points
        center = {
          x: (originalPoints[0].x + originalPoints[1].x + originalPoints[2].x) / 3,
          y: (originalPoints[0].y + originalPoints[1].y + originalPoints[2].y) / 3
        };
        
        
        // Scale points from original positions
        scaledPoints = originalPoints.map(point => ({
          x: center.x + (point.x - center.x) * zoomFactor,
          y: center.y + (point.y - center.y) * zoomFactor
        }));
        
        scaledShape = {
          ...baseShape,
          points: scaledPoints as [Point, Point, Point],
          scaleFactor: zoomFactor,
          originalDimensions: shape.originalDimensions || { points: [...triangleShape.points] }
        } as AnyShape;
        break;

      case 'line':
        // Get original dimensions
        originalDx = shape.originalDimensions?.dx || (shape.endPoint.x - shape.position.x);
        originalDy = shape.originalDimensions?.dy || (shape.endPoint.y - shape.position.y);
        scaledShape = {
          ...baseShape,
          endPoint: {
            x: shape.position.x + originalDx * zoomFactor,
            y: shape.position.y + originalDy * zoomFactor
          },
          scaleFactor: zoomFactor,
          originalDimensions: shape.originalDimensions || { 
            dx: shape.endPoint.x - shape.position.x,
            dy: shape.endPoint.y - shape.position.y
          }
        } as AnyShape;
        break;

      default:
        scaledShape = baseShape;
    }

    return scaledShape;
  });

  // Scale formulas according to zoom factor
  const _scaledFormulas = formulas.map(formula => ({
    ...formula,
    scaleFactor: (formula.scaleFactor || 1) * zoomFactor
  }));

  // Function to render parameter sliders for embedded view
  const renderParameterSliders = (formula: Formula) => {
    // Extract parameters from the formula expression
    const extractParameters = (expression: string) => {
      const params: { [key: string]: { value: number; min: number; max: number } } = {};
      
      // Prefer explicit variables provided by editor
      if (formula.variables && Object.keys(formula.variables).length > 0) {
        Object.entries(formula.variables).forEach(([k, v]) => {
          // Only include simple single-letter parameters (Unicode letters), exclude independent variable
          const independentVar = formula.independentVar || 'x';
          if (/^\p{L}$/u.test(k) && k !== independentVar) {
            params[k] = { value: v.value, min: v.min, max: v.max };
          }
        });
        return params;
      }
      
      // Only extract parameters if they make sense for the expression
      // Look for quadratic patterns: ax^2 + bx + c
      const hasQuadratic = expression.includes('x*x') || expression.includes('x^2');
      const hasLinear = /[+-]?\s*\d*\.?\d*\s*\*?\s*x(?!\s*[*^])/.test(expression);
      const hasConstant = /[+-]?\s*\d+\.?\d*(?!\s*\*?\s*x)/.test(expression);
      
      if (hasQuadratic) {
        // Extract a parameter (coefficient of x^2)
        const aMatch = expression.match(/(\d*\.?\d*)\s*\*?\s*x\s*\*?\s*x/);
        if (aMatch && aMatch[1]) {
          const aValue = parseFloat(aMatch[1]);
          if (!isNaN(aValue) && aValue !== 1) {
            params.a = { value: aValue, min: -3, max: 3 };
          }
        }
      }
      
      if (hasLinear && !expression.match(/^x$/)) {
        // Extract b parameter (coefficient of x) - but not for simple "x"
        const bMatch = expression.match(/([+-]?\s*\d+\.?\d*)\s*\*?\s*x(?!\s*[*^])/);
        if (bMatch) {
          const bValue = parseFloat(bMatch[1].replace(/\s/g, ''));
          if (!isNaN(bValue) && bValue !== 0) {
            params.b = { value: bValue, min: -5, max: 5 };
          }
        }
      }
      
      if (hasConstant) {
        // Extract c parameter (constant term)
        const cMatch = expression.match(/([+-]?\s*\d+\.?\d*)(?!\s*\*?\s*x)/);
        if (cMatch) {
          const cValue = parseFloat(cMatch[1].replace(/\s/g, ''));
          if (!isNaN(cValue) && cValue !== 0) {
            params.c = { value: cValue, min: -10, max: 10 };
          }
        }
      }
      
      return params;
    };

    const parameters = extractParameters(formula.expression);

    // Unified neutral gray styling for all sliders
    const thumbColor = '#9ca3af'; // gray-400 (lighter thumb)
    const progressStart = '#d1d5db'; // gray-300 (lighter start)
    const progressEnd = '#9ca3af'; // gray-400 (lighter end)


    // Helper to format numbers for KaTeX with decimal comma
    const toLatexNumber = (num: number, fixed?: number): string => {
      const str = typeof fixed === 'number' ? num.toFixed(fixed) : String(num);
      return str.replace(/(?<=\d)\.(?=\d)/g, '{,}');
    };

    return Object.entries(parameters).map(([param, config], _index) => {
      const sliderId = `slider-${formula.id}-${param}`;
      return (
        <div key={param} className="space-y-2">
          <div className="flex justify-between items-center">
            <label htmlFor={sliderId} className="text-sm font-medium text-gray-600" aria-label={`Parameter ${param}`}>
              Parameter <InlineMath math={param} />
            </label>
            <span className="text-sm text-gray-500">
              <InlineMath math={toLatexNumber(config.value, 1)} />
            </span>
          </div>
          <input
            id={sliderId}
            aria-label={`Parameter ${param}`}
            type="range"
            min={config.min}
            max={config.max}
            step={0.1}
            defaultValue={config.value}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value);
              // Display is driven by React via InlineMath; just update formulas
              setEmbeddedFormulas(prev => prev.map(f => {
                if (f.id !== formula.id) return f;
                const next: Formula = { ...f, variables: { ...(f.variables || {}) } };
                const existing = next.variables?.[param];
                if (existing) {
                  next.variables![param] = { ...existing, value: newValue } as any;
                } else {
                  next.variables = { ...(next.variables || {}), [param]: { value: newValue, min: config.min, max: config.max } } as any;
                }
                return next;
              }));
              // update CSS variable for progress background
              e.currentTarget.style.setProperty('--value', String(newValue));
            }}
            onMouseUp={onFirstInteractiveUse}
            onTouchEnd={onFirstInteractiveUse}
            onPointerUp={onFirstInteractiveUse}
            className="fp-range slider-progress w-full"
            style={{
              '--thumb-color': thumbColor,
              '--track-color': '#e5e7eb',
              '--progress-start': progressStart,
              '--progress-end': progressEnd,
              '--min': String(config.min),
              '--max': String(config.max),
              '--value': String(config.value)
            } as React.CSSProperties}
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span><InlineMath math={toLatexNumber(config.min)} /></span>
            <span><InlineMath math={toLatexNumber(config.max)} /></span>
          </div>
        </div>
      );
    });
  };

  // Build a human-readable expression with current variable values (embedded live view)
  const getSubstitutedExpression = useCallback((expression: string, vars?: Formula['variables']) => {
    let out = expression;
    // First: make implicit multiplication explicit for letter adjacency with x
    // ... e.g., xa -> x*a, ax -> a*x (single-letter vars)
    out = out.replace(/(?<![\p{L}0-9_])x([\p{L}])(?![\p{L}0-9_])/gu, 'x*$1');
    out = out.replace(/(?<![\p{L}0-9_])([\p{L}])x(?![\p{L}0-9_])/gu, '$1*x');

    // Then, if we have variable values, substitute them (excluding independent x)
    if (vars && Object.keys(vars).length > 0) {
      const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      for (const [name, cfg] of Object.entries(vars)) {
        if (name === 'x' || name.length !== 1) continue;
        // Unicode-aware word boundaries for single-letter variables (e.g., Greek)
        const pattern = `(?<![\\p{L}\\p{N}_])${escapeRegex(name)}(?![\\p{L}\\p{N}_])`;
        const re = new RegExp(pattern, 'gu');
        out = out.replace(re, String(cfg.value));
      }
    }

    // Finally, handle implicit numeric adjacency with x (e.g., 2x or x2)
    out = out.replace(/x\s*(\d+(?:\.\d+)?)/g, 'x*$1');
    out = out.replace(/(\d+(?:\.\d+)?)\s*x/g, '$1*x');
    return out;
  }, []);

  // Use centralized LaTeX conversion from formulaUtils
  const toLatex = useCallback((expression: string): string => {
    return convertToLatex(expression);
  }, []);

  // Keep the grid centered during initial layout settling (toolbars/fonts), then reveal
  useEffect(() => {
    if (showPositiveQuadrant) {
      // No centering concept in positive quadrant
      if (!didInit) setDidInit(true);
      return;
    }
    const el = canvasRef.current;
    if (!el || didInit === true) return;
    let stopped = false;
    const anchorDurationMs = 450;
    const anchorUntil = Date.now() + anchorDurationMs;

    const centerToCanvas = () => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const target = { x: Math.round(rect.width / 2), y: Math.round(rect.height / 2) };
      setGridPosition(target);
    };

    // Center immediately and start observing size changes during the anchor window
    centerToCanvas();
    const ro = new ResizeObserver(() => {
      if (stopped) return;
      if (Date.now() <= anchorUntil) {
        centerToCanvas();
      }
    });
    ro.observe(el);

    const endTimer = setTimeout(() => {
      stopped = true;
      ro.disconnect();
      setDidInit(true);
    }, anchorDurationMs + 50);

    return () => {
      stopped = true;
      ro.disconnect();
      clearTimeout(endTimer);
    };
  }, [showPositiveQuadrant, didInit]);

  return (
    <div className="relative w-full h-full">
      {/* Custom styles for embedded view sliders */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .embedded-view input[type="range"] {
            -webkit-appearance: none;
            appearance: none;
            background: transparent;
            cursor: pointer;
    <div className="geometry-canvas-container relative w-full h-full">
      <div 
        id="geometry-canvas"
        ref={canvasRef}
        className="canvas-container relative w-full h-full overflow-hidden"
        style={{ 
          cursor: activeMode === 'move' ? 'move' : 'default',
          pointerEvents: isNonInteractive ? 'none' : 'auto'
        }}
        tabIndex={0}
        onKeyDown={isNonInteractive ? undefined : handleKeyDown}
        onKeyUp={isNonInteractive ? undefined : handleKeyUp}
        onMouseDown={isNonInteractive ? undefined : handleMouseDown}
        onMouseMove={isNonInteractive ? undefined : handleMouseMove}
        onMouseUp={isNonInteractive ? undefined : customMouseUpHandler}
        onMouseLeave={isNonInteractive ? undefined : customMouseUpHandler}
        onClick={isNonInteractive ? undefined : (e) => {
          // Focus the canvas container when clicking on it
          focusCanvas();
          
          // If the click is on a path (part of the formula graph), don't dismiss
          if ((e.target as Element).tagName === 'path') {
            return;
          }
          
          /* Chrome/Safari */
          .embedded-view input[type="range"]::-webkit-slider-runnable-track {
            background: #e5e7eb;
            height: 8px;
            border-radius: 4px;
          }
          
          .embedded-view input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: var(--thumb-color, #6b7280);
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            /* Center the 20px thumb on an 8px track */
            margin-top: -6px;
          }
          
          /* Firefox */
          .embedded-view input[type="range"]::-moz-range-track {
            background: #e5e7eb;
            height: 8px;
            border-radius: 4px;
            border: none;
          }
          
          .embedded-view input[type="range"]::-moz-range-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: var(--thumb-color, #6b7280);
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
        `
      }} />

      {/* Embedded View Mode */}
      {isEmbedded && (
        <div className="embedded-view w-full h-full flex flex-col">
          {/* Graph + sliders card (includes header and expression) */}
          <div className="rounded-lg bg-white border border-gray-200 p-3">
            {/* Header title */}
            {showEmbeddedHeader && (
              <div className="flex items-center mb-3 pl-3">
                <div className="text-base sm:text-lg font-semibold text-gray-800">{t('interactiveFunctionGraph')}</div>
              </div>
            )}
            {/* Function Expression Display (read-only) */}
            {embeddedFormulas.length > 0 && (() => {
              const formula = embeddedFormulas[0];
              const hasVariables = formula.variables && Object.keys(formula.variables).length > 0;
              
              // Use formula's function signature or defaults
              const functionName = formula.functionName || 'f';
              const variable = formula.independentVar || 'x';
              const functionSignature = `${functionName}(${variable})`;
              
              return (
                <div className="mb-3 w-fit mx-auto">
                  {hasVariables ? (
                    <div className="flex flex-row flex-wrap items-center justify-center gap-2">
                      {/* Original Expression */}
                      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg px-3 py-2 text-center">
                        <div className="text-base text-blue-900 whitespace-pre-wrap font-medium">
                          <InlineMath math={`${functionSignature} = ${toLatex(formula.expression)}`} />
                        </div>
                      </div>
                      
                      {/* Arrow (hidden on mobile) */}
                      <div className="hidden sm:flex items-center text-blue-600">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                      
                      {/* Live Expression */}
                      <div className="bg-green-50 border-2 border-green-300 rounded-lg px-3 py-2 text-center">
                        <div className="text-base text-green-900 whitespace-pre-wrap font-medium">
                          <InlineMath math={`${functionSignature} = ${toLatex(getSubstitutedExpression(formula.expression, formula.variables))}`} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border-2 border-blue-300 rounded-lg px-3 py-2 text-center">
                      <div className="text-base text-blue-900 whitespace-pre-wrap font-medium">
                        <InlineMath math={`${functionSignature} = ${toLatex(formula.expression)}`} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Graph Canvas Container */}
            <div className="relative">
              <div 
                id="geometry-canvas"
                ref={canvasRef}
                className="canvas-container relative w-full h-72 sm:h-80 overflow-hidden border border-gray-200 rounded-md p-3 box-border bg-white"
                style={{ 
                  cursor: 'default',
                  pointerEvents: 'auto',
                  visibility: didInit ? 'visible' : 'hidden'
                }}

              >
              {/* Top-right controls: reset (fullscreen removed) - hidden when grid is centered; also hidden in positive quadrant */}
              {!showPositiveQuadrant && !isGridCentered && (
                <div className="absolute top-2 right-2 z-20 flex gap-2">
                  <button
                    type="button"
                    aria-label="Reset grid"
                    className="h-8 w-8 rounded-md bg-white/90 hover:bg-white shadow border flex items-center justify-center"
                    onClick={() => {
                      if (onResetGrid) {
                        onResetGrid();
                        return;
                      }
                      if (embeddedInitRef.current) {
                        setGridPosition(embeddedInitRef.current.grid);
                        setZoomFactor(embeddedInitRef.current.zoom);
                        setEmbeddedFormulas(JSON.parse(JSON.stringify(embeddedInitRef.current.formulas)));
                      } else if (canvasRef.current) {
                        const rect = canvasRef.current.getBoundingClientRect();
                        setGridPosition({ x: Math.round(rect.width / 2), y: Math.round(rect.height / 2) });
                        setZoomFactor(1);
                      }
                    }}
                  >
                    <span className="sr-only">Reset</span>
                    <Crosshair className="h-4 w-4" />
                  </button>
                </div>
              )}
                {/* Grid - Pass the zoomed pixel values with full coordinate system */}
                <CanvasGrid
                  key={`grid-embedded-${canvasSize.width > 0 && canvasSize.height > 0 ? 'loaded' : 'loading'}-${gridPosition ? `${Math.round(gridPosition.x)}-${Math.round(gridPosition.y)}` : 'none'}`}
                  canvasSize={canvasSize} 
                  pixelsPerCm={zoomedPixelsPerUnit} 
                  pixelsPerMm={zoomedPixelsPerSmallUnit}
                  measurementUnit={measurementUnit || 'cm'}
                  onMoveAllShapes={handleMoveAllShapes}
                  initialPosition={gridPosition}
                  onPositionChange={handleGridPositionChange}
                  positionUpdateId={positionUpdateId}
                  showZoomControls={false}
                  isNonInteractive={false}
                  forceCenterOnInit={forceCenterOnInit}
                  isEmbedded={true}
                  showUnits={showUnits}
                  showPositiveQuadrant={showPositiveQuadrant}
                />
              
                {/* Dedicated formula layer with its own SVG */}
                <div className="absolute inset-0" style={{ zIndex: 15, pointerEvents: 'none' }}>
                  <svg 
                    width="100%" 
                    height="100%" 
                    style={{ pointerEvents: 'none' }}
                  >
                    {showPositiveQuadrant ? (
                      <>
                        <defs>
                          <clipPath id="positive-quad-clip-embedded">
                            {(() => {
                              const padding = 12;
                              const bleed = 1; // expand to avoid clipping stroke
                              const clipX = padding - bleed;
                              const clipY = padding - bleed;
                              const clipWidth = Math.max(0, canvasSize.width - 2 * padding + 2 * bleed);
                              const clipHeight = Math.max(0, canvasSize.height - 2 * padding + 2 * bleed);
                              return (
                                <rect x={clipX} y={clipY} width={clipWidth} height={clipHeight} />
                              );
                            })()}
                          </clipPath>
                        </defs>
                        <g clipPath="url(#positive-quad-clip-embedded)">
                          {renderFormulas()}
                        </g>
                      </>
                    ) : (
                      renderFormulas()
                    )}
                  </svg>
                </div>
                
                {/* Axis Labels for embedded view */}
                {embeddedFormulas.length > 0 && (() => {
                  const formula = embeddedFormulas[0];
                  const isParametric = formula.type === 'parametric';
                  const isPolar = formula.type === 'polar';
                  
                  // Use formula's function signature for axis labels
                  const functionName = formula.functionName || 'f';
                  const variable = formula.independentVar || 'x';
                  
                  // Determine axis labels based on formula type and signature
                  let xAxisLabel = variable;
                  let yAxisLabel = `${functionName}(${variable})`;
                  
                  if (isParametric) {
                    xAxisLabel = 't';
                    yAxisLabel = 's(t)';
                  } else if (isPolar) {
                    xAxisLabel = 'θ';
                    yAxisLabel = 'r(θ)';
                  }
                  
                  return (
                    <>
                      {/* X-axis label (next to right arrow on x-axis) */}
                      {gridPosition && (
                        <div 
                          className="absolute z-30 text-base font-medium text-gray-700"
                          style={{
                            left: `${canvasSize.width - 20}px`, // Next to right arrow
                            top: `${gridPosition.y + 5}px` // Slightly below x-axis line
                          }}
                        >
                          {xAxisLabel}
                        </div>
                      )}
                      {/* Y-axis label (next to up arrow on y-axis) */}
                      {gridPosition && (
                        <div 
                          className="absolute z-30 text-base font-medium text-gray-700"
                          style={{
                            left: `${gridPosition.x + 10}px`, // Slightly right of y-axis line
                            top: `5px` // Next to up arrow
                          }}
                        >
                          {yAxisLabel}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {/* Parameter Sliders (interactive within provided ranges) */}
              {embeddedFormulas.length > 0 && (() => {
                const sliders = renderParameterSliders(embeddedFormulas[0]);
                return sliders.length > 0 ? (
                  <div className="mt-4 w-full px-4 sm:px-8 md:px-12 pb-1">
                    {sliders.length === 1 ? (
                      <div className="max-w-md mx-auto">{sliders[0]}</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {sliders.map((sliderEl, idx) => (
                          <div key={idx}>{sliderEl}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Standard Canvas Mode */}
      {!isEmbedded && (
        <div 
          id="geometry-canvas"
          ref={canvasRef}
          className="canvas-container relative w-full h-full overflow-hidden p-3 box-border"
          style={{ 
            cursor: activeMode === 'move' ? 'move' : 'default',
            pointerEvents: isNonInteractive ? 'none' : 'auto',
            visibility: didInit ? 'visible' : 'hidden'
          }}
          tabIndex={0}
          onKeyDown={isNonInteractive ? undefined : handleKeyDown}
          onKeyUp={isNonInteractive ? undefined : handleKeyUp}
          onMouseDown={isNonInteractive ? undefined : handleMouseDown}
          onMouseMove={isNonInteractive ? undefined : handleMouseMove}
          onMouseUp={isNonInteractive ? undefined : customMouseUpHandler}
          onMouseLeave={isNonInteractive ? undefined : customMouseUpHandler}
          onClick={isNonInteractive ? undefined : (e) => {
            // Focus the canvas container when clicking on it
            focusCanvas();
            
            // If the click is on a path (part of the formula graph), don't dismiss
            if ((e.target as Element).tagName === 'path') {
              return;
            }
            
            // If we clicked on a path in this render cycle, don't dismiss
            if (clickedOnPathRef.current) {
              return;
            }
            
            // Otherwise, clear any selected formula point
            clearAllSelectedPoints();
            
            // Reset the flag for the next click
            clickedOnPathRef.current = false;
          }}
        >
        {/* Render canvas tools */}
        {canvasTools}
        
        {/* Grid - Pass the zoomed pixel values */}
        <CanvasGrid
          key={`grid-${canvasSize.width > 0 && canvasSize.height > 0 ? 'loaded' : 'loading'}-${isFullscreen ? 'fullscreen' : 'normal'}`}
          canvasSize={canvasSize} 
          pixelsPerCm={zoomedPixelsPerUnit} 
          pixelsPerMm={zoomedPixelsPerSmallUnit}
          measurementUnit={measurementUnit || 'cm'}
          onMoveAllShapes={handleMoveAllShapes}
          initialPosition={gridPosition}
          onPositionChange={handleGridPositionChange}
          positionUpdateId={positionUpdateId}
          showZoomControls={showZoomControls}
          isNonInteractive={isNonInteractive}
          forceCenterOnInit={forceCenterOnInit}
          showUnits={showUnits}
          showPositiveQuadrant={showPositiveQuadrant}
        />
        
        {/* Render shapes with scaled values */}
        {scaledShapes.map(shape => (
          <div 
            key={shape.id}
            onClick={isNonInteractive ? undefined : () => handleShapeSelect(shape.id)}
            style={{ cursor: isNonInteractive ? 'default' : (activeMode === 'select' ? 'pointer' : 'default') }}
          >
            <ShapeRenderer
              shape={{
                ...shape,
                position: showPositiveQuadrant
                  ? { x: Math.max(0, shape.position.x), y: Math.max(0, shape.position.y) }
                  : shape.position
              }} 
              isSelected={!isNonInteractive && shape.id === selectedShapeId}
              activeMode={activeMode}
            />
            
            {/* Add rotate handlers for selected shapes only when in rotate mode - hidden in noninteractive mode */}
            {!isNonInteractive && shape.id === selectedShapeId && activeMode === 'rotate' && (
              <>
                {/* Rotate handle */}
                <div 
                  className="absolute flex items-center justify-center"
                  style={{
                    top: shape.position.y - (shape.type === 'circle' ? shape.radius : 'height' in shape ? shape.height : 0) - 20,
                    left: shape.position.x,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    width: '24px',
                    height: '24px',
                    cursor: 'ew-resize'
                  }}
                  onMouseDown={handleRotateStart}
                >
                  <div className="w-5 h-5 bg-white border-2 border-geometry-primary rounded-full flex items-center justify-center">
                    <RotateCw size={12} className="text-geometry-primary" />
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
        
        {/* Preview shape while drawing - hidden in noninteractive mode */}
        {!isNonInteractive && (
          <PreviewShape
            isDrawing={isDrawing}
            drawStart={drawStart}
            drawCurrent={drawCurrent}
            activeShapeType={activeShapeType}
            snapToGrid={isShiftPressed && !isAltPressed}
            pixelsPerSmallUnit={zoomedPixelsPerSmallUnit}
            zoomFactor={zoomFactor}
          />
        )}
        
                  {/* Dedicated formula layer with its own SVG (allow pointer events only on paths) */}
          <div className="absolute inset-0" style={{ zIndex: 15, pointerEvents: 'none' }}>
            <svg 
              width="100%" 
              height="100%" 
              style={{ pointerEvents: 'none' }}
            >
              {showPositiveQuadrant ? (
                <>
                  <defs>
                    <clipPath id="positive-quad-clip">
                      {(() => {
                        const padding = 12;
                        const bleed = 1; // expand outward slightly to avoid clipping strokes
                        // Actual origin on screen
                        const originX = gridPosition?.x ?? 0;
                        const originY = gridPosition?.y ?? canvasSize.height;
                        
                        // Start clipping at the origin (respecting left padding) and top padding
                        const clipX = Math.max(padding, originX) - bleed;
                        const clipY = padding - bleed;
                        
                        // Right edge is the right padding edge; bottom edge is the origin line
                        const rightEdge = canvasSize.width - padding + bleed;
                        const bottomEdge = originY + bleed;
                        
                        const clipWidth = Math.max(0, rightEdge - clipX);
                        const clipHeight = Math.max(0, bottomEdge - clipY);
                        
                        return (
                          <rect
                            x={clipX}
                            y={clipY}
                            width={clipWidth}
                            height={clipHeight}
                          />
                        );
                      })()}
                    </clipPath>
                  </defs>
                  <g clipPath="url(#positive-quad-clip)">
                    {renderFormulas()}
                  </g>
                </>
              ) : (
                renderFormulas()
              )}
            </svg>
          </div>
        
        {/* Display unified info panel - hidden in noninteractive mode */}
        {!isNonInteractive && persistentPanelData?.isVisible && (
          <div 
            className={`absolute w-80 unified-info-panel-container bottom-4 z-40 transition-all duration-200 ease-in-out ${
              showCalibration ? 'right-[calc(20rem+1rem)]' : 'right-4'
            }`}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onClick={(e) => { e.stopPropagation(); }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); closePersistentPanel(); }}
              onMouseDown={(e) => { e.stopPropagation(); }}
              onMouseUp={(e) => { e.stopPropagation(); }}
              className="unified-panel-close-button absolute z-50 bg-gray-200 hover:bg-gray-300 text-black flex items-center justify-center shadow-lg transition-colors"
              title="Close panel"
            >
              ×
            </button>
            <UnifiedInfoPanel
              // Point info props
              point={persistentPanelData?.type === 'point' && persistentPanelData.point ? {
                ...persistentPanelData.point,
                navigationStepSize: isShiftPressed ? 1.0 : persistentPanelData.point.navigationStepSize,
                isValid: true
              } : null}
              _gridPosition={gridPosition || undefined}
              _pixelsPerUnit={zoomedPixelsPerUnit}
              onNavigatePoint={(direction, _stepSize) => {
                // Convert the direction format from 'prev'/'next' to 'previous'/'next'
                const directionMapping: Record<string, 'previous' | 'next'> = {
                  'prev': 'previous',
                  'next': 'next'
                };
                navigateFormulaPoint(directionMapping[direction], false);
              }}
              
              // Shape info props
              selectedShape={persistentPanelData?.type === 'shape' && persistentPanelData.shapeId ? 
                shapes.find(s => s.id === persistentPanelData.shapeId) || null : null}
              measurements={currentPanelMeasurements}
              measurementUnit={measurementUnit || 'cm'}
              onMeasurementUpdate={handleMeasurementUpdate}
            />
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default GeometryCanvas;
